import { useState } from 'react';
import { Switch, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';
import classes from './ProductQRCode.module.css';

export type ProductQrStyle = 'squares' | 'dots';

export type ProductQRCodeVariant = 'standalone' | 'inline';

export interface ProductQRCodeProps {
  value: string;
  accentColor: string;
  logoImage?: string;
  showStyleToggle?: boolean;
  showArInfo?: boolean;
  size?: number;
  /** `standalone` = full QR modal; `inline` = compact block inside share dialog */
  variant?: ProductQRCodeVariant;
}

export function ProductQRCode({
  value,
  accentColor,
  logoImage,
  showStyleToggle = false,
  showArInfo = false,
  size = 260,
  variant = 'standalone',
}: ProductQRCodeProps) {
  const { t } = useTranslation();
  const [qrStyle, setQrStyle] = useState<ProductQrStyle>('squares');
  const isInline = variant === 'inline';
  const qrSize = isInline ? Math.min(size, 168) : size;

  if (!value) return null;

  return (
    <div
      className={`${classes.root} ${isInline ? classes.rootInline : classes.rootStandalone}`}
    >
      {!isInline ? (
        <>
          <Text
            className={classes.headerTitle}
            style={{ color: accentColor }}
          >
            <span style={{ position: 'relative', zIndex: 1 }}>{t('marketplace.qr.scan')}</span>
            <span
              className={classes.headerAccent}
              style={{ backgroundColor: accentColor }}
            />
          </Text>

          {showArInfo ? (
            <Text size="sm" ta="center" c="dimmed" fw={600}>
              {t('arOnlyMobile')}
            </Text>
          ) : null}

          <Text className={classes.description}>{t('marketplace.qr.description')}</Text>
        </>
      ) : null}

      <div className={`${classes.qrFrame} ${isInline ? classes.qrFrameInline : ''}`}>
        <QRCode
          logoImage={logoImage}
          logoWidth={logoImage ? (isInline ? 40 : 64) : undefined}
          logoHeight={logoImage ? (isInline ? 40 : 64) : undefined}
          logoPadding={2}
          logoPaddingStyle="square"
          bgColor="white"
          fgColor="black"
          qrStyle={qrStyle}
          size={qrSize}
          quietZone={isInline ? 6 : 10}
          removeQrCodeBehindLogo={false}
          eyeColor={[
            { outer: 'black', inner: accentColor },
            { outer: 'black', inner: accentColor },
            { outer: 'black', inner: accentColor },
          ]}
          value={value}
        />
      </div>

      {showStyleToggle ? (
        <div className={classes.styleToggle}>
          <Text className={classes.styleLabel}>{t('marketplace.qr.style')}</Text>
          <Text
            className={`${classes.styleOption} ${
              qrStyle === 'squares' ? classes.styleOptionActive : classes.styleOptionMuted
            }`}
            style={qrStyle === 'squares' ? { color: accentColor } : undefined}
          >
            {t('marketplace.qr.squares')}
          </Text>
          <Switch
            checked={qrStyle === 'dots'}
            onChange={(event) =>
              setQrStyle(event.currentTarget.checked ? 'dots' : 'squares')
            }
            color="dark"
            size="xs"
          />
          <Text
            className={`${classes.styleOption} ${
              qrStyle === 'dots' ? classes.styleOptionActive : classes.styleOptionMuted
            }`}
            style={qrStyle === 'dots' ? { color: accentColor } : undefined}
          >
            {t('marketplace.qr.dots')}
          </Text>
        </div>
      ) : null}
    </div>
  );
}
